import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@acast-tech/auth-client';
import { useApi } from '@/services/queryUtils';
import {
  episodeLaunchComparisonFetcher,
  episodePublishDatesFetcher,
  contentOverviewFetcher,
  showPlatformsFetcher,
} from './fetchers';
import type { UseQueryOptions } from '@tanstack/react-query';
import type {
  ChartGranularity,
  ContentOverviewResponse,
  EpisodeLaunchComparisonResponse,
  ItemPlatformsResponse,
  EpisodePublishDatesResponse,
  LaunchWindowDays,
  SelectedItemWithColor,
  SelectedItem,
} from '@/types/content';
import { useDerivedNetworkContext, useFilterContext } from '@/context';
import type { DateRange, NetworkAccessLevel, SelectedNetwork } from '@/types';
import { ContentQueryDataSources } from '@/routes/ContentPerformancePage/PerformanceCard/performanceCardUrlBuilders';

/**
 * Derives the `networkIds` list for the `/v2/content/*` endpoints from the selected networks.
 * In single-network (path) mode this is `[primary]`; in query mode it carries every network from
 * `?networks=`. Returns `undefined` when no network resolves, letting fetchers fall back to
 * `[networkId]` so behaviour is unchanged for callers without multi-network context.
 */
const toNetworkIds = (selectedNetworks: SelectedNetwork[] | undefined): string[] | undefined => {
  const ids = selectedNetworks?.map((network) => network.id).filter((id): id is string => !!id);
  return ids && ids.length > 0 ? ids : undefined;
};

/**
 * Resolves the network ID that owns the given show.
 * When multiple networks are selected and a specific show is filtered, the show-level API
 * endpoints must use the network that actually owns that show — not necessarily the first
 * selected network. Falls back to the first selected network's ID when `'all'` is passed
 * (no show filter active), or when the owning network cannot be determined.
 */
const resolveNetworkIdForShow = (
  selectedShow: string | 'all',
  selectedNetworks: SelectedNetwork[]
): string => {
  // We return the networkId if shows = 'all'
  const fallbackId = selectedNetworks[0]?.id;
  if (selectedShow === 'all') return fallbackId ?? '';
  const owningNetwork = selectedNetworks.find((network) =>
    network.shows.some((show) => show.id === selectedShow)
  );
  if (!owningNetwork) {
    console.error(
      `[resolveNetworkIdForShow] No owning network found for showId "${selectedShow}". Falling back to first selected network.`
    );
  }
  return owningNetwork?.id ?? fallbackId ?? '';
};

/** Shared network + filter state for content performance hooks */
const useContentQueryContext = () => {
  const { selectedNetworks, selectedNetwork } = useDerivedNetworkContext();
  const { filters } = useFilterContext();
  const { from, to } = filters.dateRange;
  const selectedShow = filters.selectedShows[0];

  const getNetworkIdForShow = useCallback(
    (showId: string | 'all') => resolveNetworkIdForShow(showId, selectedNetworks),
    [selectedNetworks]
  );

  return {
    selectedNetworks,
    networkId: selectedNetwork.id ?? '',
    networkIds: toNetworkIds(selectedNetworks),
    from,
    to,
    hasDateRange: Boolean(from && to),
    selectedShow,
    showId: selectedShow !== 'all' && selectedShow ? selectedShow : undefined,
    getNetworkIdForShow,
  };
};

export type FilterParams = {
  networks?: Array<SelectedItem & { accessLevel?: NetworkAccessLevel }>;
  shows?: SelectedItem[];
  /** All shows available across the currently selected network(s), regardless of the `shows` filter. */
  allShows?: SelectedItem[];
  platforms?: SelectedItem[];
  dateRange?: DateRange;
};

/**
 * The aim is for this to be the way to get filter values from anywhere.
 *
 * Currently it is a little hacky, but the idea is to have the interface we need and we can fill it in and make it better as we roll things out.
 * We can easily add more things if we want, but I would probably avoid that.
 * I was even a little unsure about optional dates and the platforms.
 *
 * @returns FilterParams
 */
export const useFilters = (): FilterParams => {
  const { selectedNetworks, shows: networkShows } = useDerivedNetworkContext();
  const { filters } = useFilterContext();
  const { dateRange, selectedShows, selectedPlatforms: platforms } = filters;

  const networks = selectedNetworks
    .filter(({ id, name }) => Boolean(id && name)) // TODO: are we always setting network name?
    .map((network) => ({
      id: network.id!,
      label: network.name!,
      accessLevel: network.accessLevel,
    }));

  const shows = selectedShows.length === 0 ? undefined : selectedShows.map((id) => ({ id, label: '' }));
  const allShows = (networkShows ?? []).map((show) => ({ id: show.id, label: show.name }));

  return { networks, shows, allShows, platforms, dateRange };
};

const createContentQuery = <TData, TAdditionalParams = Record<string, unknown>>(
  queryKeyPrefix: string,
  fetcher: (
    params: {
      networkId?: string;
      networkIds?: string[];
      from?: string;
      to?: string;
    } & TAdditionalParams
  ) => (token: string) => Promise<TData>
) => {
  return (
    networkId?: string,
    from?: string,
    to?: string,
    options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>,
    additionalParams?: TAdditionalParams
  ) => {
    const { networkIds } = useContentQueryContext();
    const enabled = !!networkId && !!from && !!to;
    // Key on the full network list (not just the primary id) so switching `?networks=A,B` to
    // `?networks=A,C` refetches instead of reusing a cached single-network response.
    const networkIdsKey = networkIds?.join(',') ?? networkId;

    const queryKey = additionalParams
      ? [queryKeyPrefix, networkIdsKey, from, to, ...Object.values(additionalParams)]
      : [queryKeyPrefix, networkIdsKey, from, to];

    const fetcherParams = {
      networkId,
      networkIds,
      from,
      to,
      ...(additionalParams || {}),
    } as {
      networkId: string;
      networkIds?: string[];
      from?: string;
      to?: string;
    } & TAdditionalParams;

    return useApi(queryKey, fetcher(fetcherParams), {
      ...options,
      enabled: options?.enabled ?? enabled,
    });
  };
};

export const useContentOverviewQuery = createContentQuery<
  ContentOverviewResponse,
  { showIds?: string[]; dataSource?: 'v2'|'v3' }
>('overview', contentOverviewFetcher);

type EpisodePublishDatesAdditionalParams = { showIds?: string[]; granularity?: ChartGranularity };

const useEpisodePublishDatesQueryInner = createContentQuery<
  EpisodePublishDatesResponse,
  EpisodePublishDatesAdditionalParams
>('episode-publish-dates', episodePublishDatesFetcher);

/**
 * Pre-bucketed publish dates for chart reference lines.
 * Uses `placeholderData: keepPreviousData` when granularity or filters change.
 */
export const useEpisodePublishDatesQuery = (
  networkId?: string,
  from?: string,
  to?: string,
  options?: Omit<UseQueryOptions<EpisodePublishDatesResponse, Error>, 'queryKey' | 'queryFn'>,
  additionalParams?: EpisodePublishDatesAdditionalParams
) =>
  useEpisodePublishDatesQueryInner(
    networkId,
    from,
    to,
    { placeholderData: keepPreviousData, ...options },
    additionalParams
  );

const CONTENT_STALE_TIME = 5 * 60 * 1000; // 5 minutes — content data updates nightly

const showPlatformsQueryKey = (
  networkIds: string[] | undefined,
  showId: string,
  from: string,
  to: string,
  limit?: number
) => ['show-platforms', networkIds?.join(','), showId, from, to, limit ?? 'all'] as const;

export const useShowPlatformsQuery = (
  showId: string,
  enabled: boolean,
  limit?: number
): ReturnType<typeof useApi<ItemPlatformsResponse>> => {
  const { networkIds, from, to, hasDateRange } = useContentQueryContext();

  return useApi<ItemPlatformsResponse>(
    showPlatformsQueryKey(networkIds, showId, from, to, limit),
    showPlatformsFetcher({ networkIds, showIds: [showId], from, to, limit }),
    {
      enabled: enabled && !!networkIds && hasDateRange,
      staleTime: CONTENT_STALE_TIME,
      placeholderData: keepPreviousData,
    }
  );
};

export const usePrefetchShowPlatforms = (limit: number): ((showId: string) => void) => {
  const { networkIds, from, to, hasDateRange } = useContentQueryContext();
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useCallback(
    (showId: string) => {
      if (!networkIds || !hasDateRange) return;

      queryClient.prefetchQuery({
        queryKey: showPlatformsQueryKey(networkIds, showId, from, to, limit),
        queryFn: async () => {
          const token = await getToken();
          return showPlatformsFetcher({ networkIds, showIds: [showId], from, to, limit })(token);
        },
        staleTime: CONTENT_STALE_TIME,
      });
    },
    [queryClient, getToken, networkIds, from, to, hasDateRange, limit]
  );
};

/**
 * Fetches post-launch comparison data for a batch of episodes in a single request.
 * Uses `placeholderData: keepPreviousData` so window/selection changes do not flash empty state.
 * Ignores global date filters — the comparison is relative to each episode's publish date.
 * Data only changes with the nightly pipeline run, so staleTime is Infinity.
 *
 * Passes every show from the URL/filter as `showIds`. When the show filter is empty, omits
 * showIds (same as other content queries) so the API treats it as all shows — avoids sending
 * catalogs larger than MAX_SHOWS.
 */
export const useEpisodeLaunchComparisonQuery = (
  episodes: SelectedItemWithColor[],
  windowDays: LaunchWindowDays,
  isSynced: boolean = true,
  dataSource: ContentQueryDataSources = 'v2'
): EpisodeLaunchComparisonResponse | undefined => {
  const {
    filters: { selectedShows: selectedShowIds },
  } = useFilterContext();
  const { networkId, networkIds } = useContentQueryContext();

  const showIds = useMemo(
    () => (selectedShowIds.length > 0 ? [...new Set(selectedShowIds)].sort() : undefined),
    [selectedShowIds]
  );

  const resolvedNetworkIds = useMemo(() => {
    if (networkIds && networkIds.length > 0) return networkIds;
    return networkId ? [networkId] : [];
  }, [networkIds, networkId]);

  const enabled = resolvedNetworkIds.length > 0 && episodes.length > 0 && isSynced;

  const sortedEpisodeIds = useMemo(() => episodes.map((episode) => episode.id).sort(), [episodes]);

  const result = useApi<EpisodeLaunchComparisonResponse>(
    [
      'episode-launch-comparison',
      resolvedNetworkIds,
      showIds,
      windowDays,
      sortedEpisodeIds,
      dataSource,
    ],
    (token) =>
      episodeLaunchComparisonFetcher({
        networkIds: resolvedNetworkIds,
        showIds,
        episodeIds: sortedEpisodeIds,
        windowDays,
        dataSource,
      })(token),
    {
      enabled,
      staleTime: Infinity,
      placeholderData: keepPreviousData,
    }
  );

  return result.data;
};
